// App.js
import React, { useEffect, useState, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute, AdminProtectedRoute } from './components/ProtectedRoute';
import Header from './components/Header';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Fresh from './pages/Fresh';
import Global from './pages/Global';
import AssistantIA from './pages/AssistantIA';
import Settings from './pages/Settings';
import Contact from './pages/Contact';
import ForgotPassword from './pages/ForgotPassword';
import Instructions from './pages/Instructions';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import InactivityDetector from './components/InactivityDetector';
import { decodeJWT, refreshUserToken } from './api';

// Hook pour rafraîchir automatiquement le token utilisateur
function useAutoRefreshToken() {
  const refreshTimeout = useRef(null);

  useEffect(() => {
    function scheduleRefresh() {
      const token = localStorage.getItem('token');
      if (!token) return;
      const payload = decodeJWT(token);
      if (!payload || !payload.exp) return;
      const exp = payload.exp * 1000; // JWT exp en secondes
      const now = Date.now();
      // Rafraîchir 2 minutes avant expiration
      const refreshIn = Math.max(exp - now - 2 * 60 * 1000, 5000);
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
      refreshTimeout.current = setTimeout(async () => {
        await refreshUserToken();
        scheduleRefresh(); // Replanifier après refresh
      }, refreshIn);
    }
    scheduleRefresh();
    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, []);
}

// Fonction utilitaire pour vérifier si un token est expiré
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return decoded.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Vérifier si l'utilisateur est authentifié
  useEffect(() => {
    const checkAuth = () => {
      // Vérifier et nettoyer les tokens expirés
      const token = localStorage.getItem('token');
      const adminToken = localStorage.getItem('adminToken');
      
      if (token && isTokenExpired(token)) {
        localStorage.removeItem('token');
      }
      
      if (adminToken && isTokenExpired(adminToken)) {
        localStorage.removeItem('adminToken');
      }
      
      const hasToken = !!localStorage.getItem('token') || !!localStorage.getItem('adminToken');
      setIsAuthenticated(hasToken);
    };
    
    // Vérifier au chargement initial
    checkAuth();
    
    // Écouter les changements de stockage localStorage
    const handleStorageChange = () => {
      checkAuth();
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Nettoyer l'écouteur d'événements
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  useAutoRefreshToken();

  return (
    <Router>
      <div className="app">
        <Header />
        {/* Intégrer InactivityDetector seulement quand l'utilisateur est authentifié */}
        {isAuthenticated && <InactivityDetector />}
        <main>
          <Routes>
            {/* Routes publiques */}
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Routes protégées pour utilisateurs connectés */}
            <Route element={<ProtectedRoute />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/fresh" element={<Fresh />} />
              <Route path="/global" element={<Global />} />
              <Route path="/assistant-ia" element={<AssistantIA />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/instructions" element={<Instructions />} />
            </Route>
            
            {/* Routes protégées pour administrateurs */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route element={<AdminProtectedRoute />}>
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
            </Route>
            
            {/* Redirection pour toute autre route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

export default App;
